'use client';

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../../i18n';
import { Button } from '../Button';

export type DrawerPlacement = 'left' | 'right' | 'top' | 'bottom';
export type DrawerWidth = 'default' | 'wide' | 'pos';
export type DrawerHeight = 'default' | 'tall';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  placement?: DrawerPlacement;
  width?: DrawerWidth;
  /**
   * top/bottom placement only (mirrors how `width` is ignored for top/bottom):
   * `default` keeps the existing fixed `h-72` BottomSheet size (POS quick
   * actions, etc. -- byte-identical unless a caller opts in). `tall` swaps to
   * `max-h-[85vh]` for content-rich bottom sheets (e.g. a full edit form) that
   * need to grow with their content and scroll internally rather than being
   * clipped at a fixed 288px.
   */
  height?: DrawerHeight;
  title?: ReactNode;
  children: ReactNode;
  ariaLabel?: string;
  /**
   * When true, suppresses the built-in padding (`p-8`), built-in title h2,
   * and built-in footer close button, rendering ONLY the children inside the
   * positioned panel. The caller owns header/body/footer chrome. When false
   * or absent, behavior is byte-identical to the pre-chromeless primitive.
   */
  chromeless?: boolean;
  /**
   * When true, the scrim is a flat dim overlay (no backdrop blur). POS
   * surfaces want a dim scrim, not the glass blur the default dialog uses —
   * a server mid-service must keep the check legible behind the panel.
   * Defaults to false (the blurred halo scrim, byte-identical to before).
   */
  dimScrim?: boolean;
  /** Overlay z-index utility class. Default 'z-modal'. Raise above 'z-assistant' for overlays spawned inside the assistant dock. */
  zIndexClass?: string | undefined;
}

const PLACEMENT_CLASSES: Record<DrawerPlacement, string> = {
  left: 'inset-y-0 left-0  rounded-r-control',
  right: 'inset-y-0 right-0 rounded-l-control',
  top: 'inset-x-0 top-0   rounded-b-control',
  bottom: 'inset-x-0 bottom-0 rounded-t-control',
};

const TOP_BOTTOM_HEIGHT_CLASSES: Record<DrawerHeight, string> = {
  default: 'h-72',
  tall: 'max-h-[85vh]',
};

const SIDE_WIDTH_CLASSES: Record<DrawerWidth, string> = {
  default: 'w-80',
  wide: 'w-[min(900px,100vw)]',
  // POS modifier drawer: roomy enough for tap-tile groups without the
  // 900px 'wide' shape that would swallow the check behind it.
  pos: 'w-[min(520px,92vw)]',
};

const PLACEMENT_ANIM: Record<DrawerPlacement, string> = {
  left: 'animate-drawer-in-left',
  right: 'animate-drawer-in-right',
  top: 'animate-drawer-in-top',
  bottom: 'animate-drawer-in-bottom',
};

/** Focusable element selectors for Tab trap cycling. */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Drawer({
  open,
  onClose,
  placement = 'right',
  width = 'default',
  height = 'default',
  title,
  children,
  ariaLabel,
  chromeless = false,
  dimScrim = false,
  zIndexClass = 'z-modal',
}: DrawerProps) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const prev = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Store focus origin so we can restore it on close.
    prev.current = document.activeElement as HTMLElement | null;
    ref.current?.focus();

    // Body scroll lock — prevent background page scroll while drawer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Tab trap: cycle focus within the panel.
      if (e.key === 'Tab' && ref.current) {
        const focusable = Array.from(
          ref.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.closest('[disabled]'));
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (e.shiftKey) {
          if (document.activeElement === first || document.activeElement === ref.current) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prev.current?.focus();
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const scrim =
    placement === 'right' || placement === 'left'
      ? 'var(--overlay-scrim-right)'
      : 'var(--overlay-scrim)';

  const panelPaddingClass = chromeless ? '' : 'p-8';

  // POS surfaces use a flat dim scrim (no blur) so the check stays legible
  // behind the drawer; the default dialog keeps its blurred halo.
  // NOTE: do not set element `opacity` here — animate-overlay-scrim-in keyframe
  // animates opacity 0→1 with fill-mode:both (CSS animation layer overrides inline
  // style). Instead encode the alpha directly in the background color.
  const scrimStyle = dimScrim
    ? { background: 'rgba(0, 0, 0, 0.4)' }
    : {
        background: scrim,
        backdropFilter: 'blur(var(--scrim-blur))',
        WebkitBackdropFilter: 'blur(var(--scrim-blur))',
      };

  return createPortal(
    <div
      className={`fixed inset-0 ${zIndexClass} animate-overlay-scrim-in`}
      style={scrimStyle}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        {...(title ? { 'aria-labelledby': titleId } : ariaLabel ? { 'aria-label': ariaLabel } : {})}
        tabIndex={-1}
        style={{ boxShadow: 'var(--shadow-dialog)' }}
        className={`absolute bg-surface-elevated outline-none ${panelPaddingClass} ${PLACEMENT_CLASSES[placement]}${placement === 'left' || placement === 'right' ? ` ${SIDE_WIDTH_CLASSES[width]}` : ` ${TOP_BOTTOM_HEIGHT_CLASSES[height]}`} ${PLACEMENT_ANIM[placement]}${chromeless ? ' flex flex-col' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!chromeless && title && (
          <h2 id={titleId} className="text-heading-2 text-primary mb-4">
            {title}
          </h2>
        )}
        {chromeless ? children : <div>{children}</div>}
        {!chromeless && (
          <div className="mt-6 flex justify-end">
            <Button variant="ghost" onClick={onClose}>
              {t('drawer.close')}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
